import { cva } from 'class-variance-authority';
import {
  Ban,
  Archive,
  CheckCircle2,
  Clock,
  FileText,
  Send,
  ShieldCheck,
  Eye,
  type LucideIcon,
} from 'lucide-react';
import type { CampaignStatus, LintSeverity } from '@/lib/schemas';
import { cn } from '@/lib/utils';

/**
 * The canonical fill/ink pairing, driven by a status → variant map rather than ad-hoc colours per
 * call site.
 *
 * Every pill carries an ICON as well as a colour. Colour alone fails for colour-blind users and in
 * greyscale print, and "Sent" versus "Sending" is exactly the distinction someone needs to get
 * right at a glance.
 *
 * The `-ink` token on the `-bg` token, always — `--danger` on `--danger-bg` is readable in light
 * mode and unreadable on the dark wash, which is the whole reason the split exists.
 */
const pill = cva(
  'inline-flex items-center gap-1.5 rounded-pill px-2 py-0.5 text-xs font-medium whitespace-nowrap',
  {
    variants: {
      tone: {
        neutral: 'bg-chip text-muted',
        info: 'bg-info-bg text-info-ink',
        success: 'bg-success-bg text-success-ink',
        warning: 'bg-warning-bg text-warning-ink',
        danger: 'bg-danger-bg text-danger-ink',
      },
    },
    defaultVariants: { tone: 'neutral' },
  },
);

type Tone = 'neutral' | 'info' | 'success' | 'warning' | 'danger';

const statusMap: Record<CampaignStatus, { tone: Tone; label: string; Icon: LucideIcon }> = {
  Draft: { tone: 'neutral', label: 'Draft', Icon: FileText },
  InReview: { tone: 'info', label: 'In review', Icon: Eye },
  Approved: { tone: 'info', label: 'Approved', Icon: ShieldCheck },
  Scheduled: { tone: 'warning', label: 'Scheduled', Icon: Clock },
  Sending: { tone: 'warning', label: 'Sending', Icon: Send },
  Sent: { tone: 'success', label: 'Sent', Icon: CheckCircle2 },
  Archived: { tone: 'neutral', label: 'Archived', Icon: Archive },
  // Danger rather than neutral: a cancelled send is an event somebody decided on, often mid-flight,
  // and it should not read like a filing state.
  Cancelled: { tone: 'danger', label: 'Cancelled', Icon: Ban },
};

export function CampaignStatusPill({ status }: { status: CampaignStatus }) {
  const { tone, label, Icon } = statusMap[status];

  return (
    <span className={pill({ tone })}>
      <Icon className="h-3 w-3" aria-hidden="true" />
      {label}
    </span>
  );
}

const severityMap: Record<LintSeverity, { tone: Tone; label: string }> = {
  Fatal: { tone: 'danger', label: 'Fatal' },
  Warning: { tone: 'warning', label: 'Warning' },
  Info: { tone: 'info', label: 'Info' },
};

export function SeverityPill({ severity, count }: { severity: LintSeverity; count?: number }) {
  const { tone, label } = severityMap[severity];

  return (
    <span className={pill({ tone })}>
      {count === undefined ? label : `${count} ${label.toLowerCase()}`}
    </span>
  );
}

export function Pill({
  tone,
  children,
  className,
}: {
  tone?: Tone;
  children: React.ReactNode;
  className?: string;
}) {
  return <span className={cn(pill({ tone }), className)}>{children}</span>;
}
